/// <amd-module name="DistributedTaskUI/LogReader/Reader" />
import * as React from "react";

import * as Resources from "DistributedTaskUI/LogReader/Resources";

import { Parser } from "DistributedTaskUI/LogReader/Parser";

import { ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { format } from "VSS/Core/Util/String";

import { VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";


import { Button } from "VSSUI/Button";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";
import { Intersection, IntersectionContext, IIntersectionContext } from "VSSUI/Intersection";
import { Observer } from "VSSUI/Observer";
import { TextField } from "VSSUI/TextField";

import { IReaderProps } from "./Reader.Props";
import { ILine, IFindResult } from "./Types";
import { getText } from "./SubParser";

interface IReaderState extends ILineLimits {
    content: string;
    lines: ILine[];
    findResult: IFindResult | undefined;
}

interface ILineLimits {
    lineStart: number;
    lineEnd: number;
}

const perfArea = "Pipelines";
const perfLogParsingScenario = "Pipelines.LogParsing";

const minLineSize = 150;
const debounceTime = 100;

const mainContentClass = "main-content";

/**
 * Hosts log component, note that this is made to work inside a panel, some calculations might have to check to work else where
 * This virtualizes lines assuming that lineSize can cover the screen. Starting with high number is fine with us, so this implementation is okay.
 * There will be 'lineSize' lines to the top and bottom of sentinel, so 'lineSize' * 2 lines, like a sliding window
 */
export class Reader extends VssComponent<IReaderProps, IReaderState> {
    // sentinel at the bottom
    private _bsentinel: HTMLSpanElement | null;
    // sentinel at the top
    private _tsentinel: HTMLSpanElement | null;
    private _intersectionContext: IIntersectionContext;

    private _parser: Parser;

    private _container: HTMLDivElement | null;
    private _lineSize: number = minLineSize;

    private _timer: TimerManagement;

    private _sentinelLine: number | undefined;
    private _findLine: number | undefined;
    private _scrollPending: boolean = false;
    private _initialzed = false;

    private _find: Find | null;

    private _showFind = new ObservableValue<boolean>(false);

    constructor(props: IReaderProps) {
        super(props);
        this._timer = new TimerManagement();
        this._parser = new Parser();
        this.state = {
            content: "",
            lines: [],
            findResult: undefined,
            lineStart: 0,
            lineEnd: 0
        };

        this._onResize = this._timer.debounce(this._onResize, debounceTime);
        this._onIntersect = this._timer.throttle(this._onIntersect, debounceTime);
        this._onKeyDown = this._timer.throttle(this._onKeyDown, debounceTime);
    }

    public render() {
        // note: we have a test case in AnsiParser.test.ts that tests for XSS
        /* tslint:disable:react-no-dangerous-html */
        let lineNumber = this.state.lineStart;
        const linesToRender = this.state.lines.slice(this.state.lineStart - 1, this.state.lineEnd);
        return <div className="dt-reader flex-row" tabIndex={0} ref={r => this._container = r} onKeyDown={this._onKeyDown}>
            <Intersection>
                <div className={`${mainContentClass} flex-grow scroll-auto`}>
                    <IntersectionContext.Consumer>
                        {
                            intersectionContext => {
                                this._intersectionContext = intersectionContext;
                                // stop complaining tslint! our rules don't let to do the things we do here, really?
                                // we follow this ReleaseManagement/bld/CDPScripts/tslint.json apparently, so with out this, it will fail RM.Build.UX-TsLint...
                                /* tslint:disable whitespace no-unused-expression semicolon*/
                                return <div className={`flex-column content-area ${this.props.onLineClick ? "linkable" : ""}`}>
                                    <Observer showFind={this._showFind}>
                                        {(props: { showFind: boolean }) => {
                                            if (props.showFind) {
                                                return <Find
                                                    ref={x => this._find = x}
                                                    timer={this._timer}
                                                    content={this.state.content}
                                                    lines={this.state.lines}
                                                    start={this.state.lineStart}
                                                    end={this.state.lineEnd}
                                                    onFind={this._onFind}
                                                    hide={this._onFindHide} />;
                                            }

                                            return null;
                                        }}
                                    </Observer>

                                    <FocusZone direction={FocusZoneDirection.Vertical}>
                                        <ol>
                                            {
                                                linesToRender.map((lineData, index) => {
                                                    const currentLine = lineNumber++;
                                                    const extraClassName = this.props.lineToScroll === currentLine ? "highlight" : "";
                                                    return <>
                                                        {
                                                            index === 0 && (
                                                                <li aria-hidden={true}>{this._getSentinel(currentLine, true)}</li>
                                                            )
                                                        }
                                                        <li className={`line-area flex-row ${extraClassName}`} data-line={currentLine}>
                                                            <span className="flex-noshrink line" {...this.props.onLineClick ? {
                                                                onClick: this._onLineClick
                                                            } : {}}>
                                                                {currentLine}
                                                            </span>

                                                            <span className="content" dangerouslySetInnerHTML={{
                                                                __html: this._parser.transform(lineData, this.state.content, currentLine, this.state.findResult)
                                                            }}>
                                                            </span>
                                                        </li>
                                                        {
                                                            index === linesToRender.length - 1 && this.state.lines.length > linesToRender.length && (
                                                                <li aria-hidden={true}>{this._getSentinel(currentLine)}</li>
                                                            )
                                                        }
                                                    </>;
                                                })
                                            }
                                        </ol>
                                    </FocusZone>
                                </div>;
                                /* tslint:enable whitespace no-unused-expression semicolon*/
                            }
                        }
                    </IntersectionContext.Consumer>
                </div>
            </Intersection>
        </div >;
        /* tslint:enable:react-no-dangerous-html */
    }

    public componentDidMount() {
        if (this._intersectionContext) {
            this._intersectionContext.register(this._onIntersect);
        }

        window.addEventListener("resize", this._onResize);
        this._setHeight();
        this._setContent();

        if (this.props.lineToScroll) {
            this._scrollPending = true;
        }

        if (this._container) {
            this._container.focus();
        }
    }

    public componentWillUnmount() {
        if (this._intersectionContext) {
            this._intersectionContext.unregister(this._onIntersect);
        }

        window.removeEventListener("resize", this._onResize);
    }

    public componentDidUpdate(prevProps: IReaderProps) {
        if (this.props.content !== prevProps.content) {
            this._setContent();
        }

        this._scrollToLine();
    }

    private get _perfService() {
        return this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
    }

    private _onFind = (result?: IFindResult) => {
        if (result && result.lines.length > 0) {
            this._findLine = result.toScroll;
            this._scrollPending = true;
            this._scrollToLine(result);
        }
        else {
            this.setState({
                findResult: undefined
            });
        }
    }

    private _onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        // @todo (yaananth) handle this on mobile? Should we introduce a context menu?
        const key = getText(event.key);
        if (this._container) {
            if ((event.metaKey || event.ctrlKey) && key === "f") {
                this._showFind.value = true;
                if (this._find) {
                    this._find.focus();
                }
                event.preventDefault();
            }
        }
    }

    private _onLineClick = (event: React.MouseEvent<HTMLSpanElement>) => {
        if (this.props.onLineClick && event) {
            const lineElement = (event.target as HTMLSpanElement);
            const number = parseInt(lineElement.innerText);
            this.props.onLineClick(number);
        }
    }

    private _scrollToLine(result?: IFindResult) {
        const lineToScroll = this._findLine || this._sentinelLine || this.props.lineToScroll;
        if (this._initialzed && this._scrollPending && lineToScroll && this._container && this.state.lines.length > 0) {
            if (this._isLineInLimits(lineToScroll)) {
                // line is with in our limits, simply focus
                const element = this._container.querySelector(`[data-line="${lineToScroll}"]`);
                if (element) {
                    if (this._hasScroll()) {
                        let align = "end";
                        if (lineToScroll === this.props.lineToScroll) {
                            align = "center";
                            if (this.props.onScrollToLine) {
                                this.props.onScrollToLine(lineToScroll);
                            }
                        }
                        if (lineToScroll === this._findLine) {
                            this._findLine = undefined;
                        }
                        element.scrollIntoView({
                            behavior: "auto",
                            block: align as "end" //forcing as we store it as string, we have "end", "center", "start", "nearest"
                        });
                    }

                    this._scrollPending = false;
                    // as scroll is done, now start observing again
                    if (this._bsentinel) {
                        this._intersectionContext.observe(this._bsentinel);
                    }
                    if (this._tsentinel) {
                        this._intersectionContext.observe(this._tsentinel);
                    }
                }
            }

            const newLimits = this._getLineLimits(lineToScroll, this.state.lines);
            if (newLimits) {
                this.setState({
                    lineStart: newLimits.lineStart,
                    lineEnd: newLimits.lineEnd,
                    findResult: result ? result : this.state.findResult
                });
            }
            else {
                this._scrollPending = false;
            }
        }
    }

    private _getLineLimits(lineToScroll: number | undefined, lines: ILine[]): ILineLimits | undefined {
        let limits: ILineLimits | undefined;
        if (lineToScroll && lineToScroll <= lines.length) {
            // line is not with in limits, fetch correct window
            limits = {
                lineStart: Math.max(lineToScroll - this._lineSize, 1),
                lineEnd: Math.min(lineToScroll + this._lineSize, lines.length)
            };
        }

        return limits;
    }

    private _isLineInLimits(lineToScroll: number) {
        return lineToScroll >= this.state.lineStart && lineToScroll <= this.state.lineEnd;
    }

    private _setContent() {
        this._perfService.startScenario(perfLogParsingScenario, false);
        this._initialzed = false;
        if ((this.props.content as Partial<Promise<string>>).then) {
            (this.props.content as Promise<string>).then((data) => {
                this._onData(data);
            });
        }
        else {
            this._onData((this.props.content as string));
        }
    }

    private _onData(data: string) {
        this._perfService.addSplitTiming("ContentFetched", perfLogParsingScenario);
        const content = data || "";
        const lines = this._parser.parse(content);
        if (this._container) {
            // adjust lize size to fill the view
            const parent = this._container.parentElement;
            if (parent) {
                const height = this._container.clientHeight;
                const possibleLizeSize = Math.ceil(height / 10) + 30;
                this._lineSize = Math.max(possibleLizeSize, this._lineSize);
            }
        }

        this._initialzed = true;

        const defaultLimits = this._getLineLimits(this.props.lineToScroll, lines);
        const lineStart = defaultLimits ? defaultLimits.lineStart : 1;
        const lineEnd = defaultLimits ? defaultLimits.lineEnd : Math.min(this._lineSize, lines.length);
        this.setState({
            content: content,
            lines: lines,
            lineStart: this.state.lineStart ? this.state.lineStart : lineStart,
            lineEnd: this.state.lineEnd ? this.state.lineEnd : lineEnd
        });

        if (this._container) {
            // for auto scroll
            if (this.props.autoScroll) {
                const contentElement = this._container.querySelector("." + mainContentClass);
                if (contentElement && this._hasScroll(contentElement)) {
                    if (contentElement.scroll) {
                        contentElement.scroll(0, contentElement.scrollHeight);
                    }
                    else {
                        // Edge doesn't support "scroll"...
                        if (this._container) {
                            const element = this._container.querySelector(`[data-line="${this.state.lineEnd}"]`);
                            if (element) {
                                element.scrollIntoView({
                                    behavior: "smooth",
                                    block: "end"
                                });
                            }
                        }
                    }
                }
            }
        }

        this._perfService.endScenario(perfArea, perfLogParsingScenario, false, undefined, {
            lineCount: this.state.lines.length,
            lineStart: this.state.lineStart,
            lineEnd: this.state.lineEnd,
            contentLength: this.state.content.length,
            autoScroll: !!this.props.autoScroll,
            lineToScroll: this.props.lineToScroll,
            sentinelLine: this._sentinelLine
        });
    }

    private _hasScroll(contentElement?: Element) {
        const element = contentElement || (this._container ? this._container.querySelector("." + mainContentClass) : undefined);
        return element && element.scrollHeight > element.clientHeight;
    }

    private _getSentinel(number: number, isTop: boolean = false): JSX.Element | null {
        return <span className="sentinel" data-line={number} ref={x => { this._observe(x, isTop); }}></span>;
    }

    private _onIntersect = (entries: IntersectionObserverEntry[]) => {
        if (this._scrollPending || !this._initialzed) {
            return;
        }

        for (const entry of entries) {
            const sentinelLine = parseInt(entry.target.getAttribute("data-line") || "");
            if (entry.target === this._bsentinel) {
                if (entry.intersectionRatio > 0) {
                    // bottom sentinel just came into view, move window down, we are not at bottom most, so
                    if (this.state.lineEnd === sentinelLine && this.state.lineEnd !== this.state.lines.length) {
                        this._sentinelLine = sentinelLine;
                        this._scrollPending = true;
                        // unobserve since scroll is pending
                        this._intersectionContext.unobserve(this._bsentinel);

                        const newEnd = Math.min(this.state.lineEnd + this._lineSize, this.state.lines.length);

                        this.setState({
                            lineStart: Math.max(newEnd - (this._lineSize * 2), 1),
                            lineEnd: newEnd
                        });
                    }
                }
            }
            else if (entry.target === this._tsentinel) {
                if (entry.intersectionRatio > 0) {
                    // top sentinel just came into view, move window up, we are not at top most
                    if (this.state.lineStart === sentinelLine && this.state.lineStart !== 1) {
                        this._sentinelLine = sentinelLine;
                        this._scrollPending = true;
                        // unobserve since scroll is pending
                        this._intersectionContext.unobserve(this._tsentinel);

                        const newStart = Math.max(this.state.lineStart - this._lineSize, 1);

                        this.setState({
                            lineStart: newStart,
                            lineEnd: Math.min(newStart + (this._lineSize * 2), this.state.lines.length)
                        });
                    }
                }
            }
        }
    }

    private _observe(element: HTMLSpanElement | null, isTop: boolean = false) {
        if (isTop) {
            this._tsentinel = element;
            if (this._tsentinel && this._intersectionContext) {
                this._intersectionContext.unobserve(this._tsentinel);
                this._intersectionContext.observe(this._tsentinel);
            }
        }
        else {
            this._bsentinel = element;
            if (this._bsentinel && this._intersectionContext) {
                this._intersectionContext.unobserve(this._bsentinel);
                this._intersectionContext.observe(this._bsentinel);
            }
        }
    }

    private _onResize = () => {
        this._setHeight();
    }

    private _onFindHide = () => {
        this._showFind.value = false;
    }

    private _setHeight() {
        if (this._container) {
            // for virtualization to work, the container should have fixed height mapping to the parent
            const parent = this._container.parentElement;
            if (parent) {
                // 15 offset to ignore the header
                this._container.style.height = (parent.clientHeight - 15) + "px";
            }
        }
    }
}

interface IFindProps {
    timer: TimerManagement;
    content: string;
    lines: ILine[];
    start: number;
    // We use end to determine the window size to store find results, but not to end the search
    end: number;
    onFind: (result: IFindResult | undefined) => void;
    hide: () => void;
}

export class Find extends React.Component<IFindProps> {
    private _value: ObservableValue<string>;
    private _prevValue: string | undefined;
    private _start: number;
    private _end: number;
    private _originalStart: number;
    private _textField: TextField | null;
    private _toScrollIndex: number = 0;

    private _result: IFindResult | undefined;

    constructor(props: IFindProps) {
        super(props);
        this._value = new ObservableValue<string>("");
        this._resetSeekers();
        this._originalStart = this._start;
    }

    public render() {
        return <FocusZone direction={FocusZoneDirection.Horizontal}>
            <div className="dt-command dt-find flex-row" onKeyDown={this._onKeyDown}>
                <TextField
                    ref={x => this._textField = x}
                    placeholder={Resources.SearchKeyword}
                    value={this._value}
                    onChange={this._onChange}
                />
                <span className="f-result f-mar">
                    {this._getResult()}
                </span>
                <Button
                    onClick={this._onUp}
                    className="f-icon f-mar"
                    iconProps={{ iconName: "ChevronUp" }}
                />
                <Button
                    onClick={this._onDown}
                    className="f-icon f-mar"
                    iconProps={{ iconName: "ChevronDown" }}
                />
                <Button
                    onClick={this._hide}
                    className="f-icon"
                    iconProps={{ iconName: "ChromeClose" }}
                />
            </div>
        </FocusZone>;
    }

    public focus() {
        if (this._textField) {
            this._textField.focus();
        }
    }

    public componentDidMount() {
        if (this._textField) {
            this._textField.focus();
        }
    }

    private _onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (getText(event.key) === "escape") {
            this._hide();
            event.preventDefault();
        }
    }

    private _getResult = () => {
        if (this._result) {
            return format(Resources.ResultsText, this._result.scrollCount, this._result.lines.length);
        }

        return Resources.NoResults;
    }

    private _hide = () => {
        this.props.hide();
    }

    private _onChange = (event: React.ChangeEvent<HTMLElement>, value: string) => {
        this._value.value = value;
        if (this._originalStart !== this.props.start || this._value.value !== this._prevValue) {
            // pages changed or value changed, reset
            this._resetSeekers();
        }

        this._prevValue = value;
        let result: IFindResult | undefined;
        if (value) {
            const lines = this.props.lines;
            const size = this._end - this._start;
            let currentFindSize = 0;
            let findResultLines: number[] = [];
            for (let lineIndex = this._start - 1; lineIndex < this._end; lineIndex++) {
                const line = lines[lineIndex];
                let foundMatch = false;
                const nodesCount = line.nodes.length;
                if (nodesCount > 0) {
                    const start = line.nodes[0].start;
                    const end = line.nodes[nodesCount - 1].end;
                    const data = getText(this.props.content.substring(start, end));
                    if (data.indexOf(getText(value)) !== -1) {
                        foundMatch = true;
                    }
                }

                if (foundMatch) {
                    currentFindSize++;
                    const lineNumber = lineIndex + 1;
                    findResultLines.push(lineNumber);
                    if (currentFindSize === size) {
                        // reached the window size, update next window and break
                        this._start = lineNumber;
                        this._end = (lineNumber + size) % lines.length;
                        break;
                    }
                }
            }

            if (findResultLines.length > 0) {
                result = {
                    lines: findResultLines,
                    text: value,
                    toScroll: findResultLines[this._toScrollIndex],
                    scrollCount: this._toScrollIndex + 1
                };
            }
        }

        this._result = result;
        this.props.onFind(result);
    }

    private _onDown = () => {
        if (this._result) {
            const toChange = this._toScrollIndex + 1;
            this._toScrollIndex = toChange >= this._result.lines.length ? 0 : toChange;
            this._updateResult(this._toScrollIndex);
            this.props.onFind(this._result);
        }
    }

    private _onUp = () => {
        if (this._result) {
            const toChange = this._toScrollIndex - 1;
            this._toScrollIndex = toChange < 0 ? this._result.lines.length - 1 : toChange;
            this._updateResult(this._toScrollIndex);
            this.props.onFind(this._result);
        }

    }

    private _updateResult(index: number) {
        if (this._result) {
            this._result.toScroll = this._result.lines[this._toScrollIndex];
            this._result.scrollCount = this._toScrollIndex + 1;
        }
    }

    private _resetSeekers() {
        this._start = 1;
        this._end = this.props.lines.length;
        this._toScrollIndex = 0;
    }
}